import { useState, useEffect, useMemo } from 'react';
import {
  fetchCompletedFinalVistaJudgments,
  fetchVistaAspects,
  fetchVistaRubrics,
} from 'services/judgmentService';
import {
  mergeJudgmentData,
  transformStrapiDataToTableFormat,
} from 'services/judgmentDataTransformer';
import { getFinalSubmissions } from 'services/finalEvaluatorService';
import { getFormSchema } from 'forms';
import { useAuth } from 'contexts/AuthContext';
import { useRoleInfo } from 'hooks/useRoleInfo';
import { Submission, Aspect, Rubric } from 'types/evaluation';
import {
  filterBySearchQuery,
  filterByCategory,
  filterByRole,
  sortSubmissions,
} from 'utils/submissionSorting';

interface Stats {
  total: number;
  judged: number;
  totalEvaluations: number;
  averageScore: number;
}

const currentYear = new Date().getFullYear();

export const useFinalJudgmentData = () => {
  const [year, setYear] = useState<number>(currentYear);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [sortField, setSortField] = useState<string>('avg_rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [aspects, setAspects] = useState<Aspect[]>([]);

  const [rubrics, setRubrics] = useState<Rubric[]>([]);

  // Get authenticated user info and role data from localStorage
  useAuth();

  const {
    userPillar,
    userCategory,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
  } = useRoleInfo();

  // Determine access control based on role type - ADMIN ONLY ACCESS
  const userHasAccess = userIsSuperAdmin; // Only ADMIN users can access this page

  const loadFinalSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        finalSubmissionsResponse,
        judgmentsResponse,
        aspectsResponse,
        rubricsResponse,
      ] = await Promise.all([
        getFinalSubmissions(year),
        fetchCompletedFinalVistaJudgments(year),
        fetchVistaAspects(year),
        fetchVistaRubrics(year),
      ]);

      const submissionsData =
        (finalSubmissionsResponse as any).data || finalSubmissionsResponse;
      const judgmentsData =
        (judgmentsResponse as any).data || judgmentsResponse;

      const aspectsData = (aspectsResponse as any).data || aspectsResponse;
      const rubricsData = (rubricsResponse as any).data || rubricsResponse;

      const transformedSubmissions =
        transformStrapiDataToTableFormat(submissionsData);
      const submissionsWithJudgments = mergeJudgmentData(
        transformedSubmissions,
        judgmentsData,
        aspectsData,
        rubricsData
      );

      // Since we're only fetching completed judgments, all submissions with judgments are valid
      const completedSubmissions = submissionsWithJudgments.filter(
        (submission: Submission) =>
          submission.all_judgments && submission.all_judgments.length > 0
      );

      setSubmissions(completedSubmissions);
      setAspects(aspectsData);
      setRubrics(rubricsData);
    } catch (err: any) {
      console.error('❌ Error loading final judgment data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFinalSubmissions();
  }, [year]);

  const handleRefresh = async () => {
    await loadFinalSubmissions();
  };

  const toggleRowExpansion = (id: string | number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const schema = getFormSchema('smdd');
  const categories = schema.categories;

  const filteredAndSortedSubmissions = useMemo(() => {
    // 1. Text search filter
    const afterSearch = searchQuery
      ? filterBySearchQuery(submissions, searchQuery)
      : submissions;

    // 2. Category dropdown filter
    const afterCategory = filterCategory
      ? filterByCategory(afterSearch, filterCategory)
      : afterSearch;

    // 3. Role-based access control
    const afterRole = filterByRole(afterCategory, {
      userIsSuperAdmin,
      userIsPillarPIC,
      userIsCategoryJudge,
      userPillar,
      userCategory,
    });

    // 4. Sort
    return sortSubmissions(afterRole, submissions, sortField, sortDirection);
  }, [
    submissions,
    searchQuery,
    filterCategory,
    sortField,
    sortDirection,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
    userPillar,
    userCategory,
  ]);

  // Calculate statistics
  const stats: Stats = useMemo(() => {
    const total = filteredAndSortedSubmissions.length;
    const judged = filteredAndSortedSubmissions.filter(
      s => s.total_score !== null && s.total_score !== undefined
    ).length;

    // Calculate total evaluations (same as Raw Judgment sheet records)
    // Each submission without judgments = 1 row, each judgment = 1 row
    const totalEvaluations = filteredAndSortedSubmissions.reduce(
      (count, submission) => {
        const allJudgments = submission.all_judgments || [];
        if (allJudgments.length === 0) {
          return count + 1; // One row for submission without judgments
        } else {
          return count + allJudgments.length; // One row per judgment
        }
      },
      0
    );

    const avgScore =
      judged > 0
        ? filteredAndSortedSubmissions
            .filter(s => s.total_score !== null && s.total_score !== undefined)
            .reduce(
              (sum, s) => sum + (parseFloat(s.total_score as string) || 0),
              0
            ) / judged
        : 0;

    return {
      total,
      judged,
      totalEvaluations,
      averageScore: Math.round(avgScore * 100) / 100,
    };
  }, [filteredAndSortedSubmissions]);

  const getMainDescription = (submission: Submission): string => {
    return (
      submission.leadership_achievement ||
      submission.project_name ||
      'No description available'
    );
  };

  const sanitizeAIText = (text: string | null | undefined): string => {
    if (!text) return 'No feedback available';
    return text.replace(/[*#]/g, '').trim();
  };

  return {
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterCategory,
    setFilterCategory,
    year,
    setYear,
    sortField,
    sortDirection,
    expandedRows,
    handleRefresh,
    toggleRowExpansion,
    handleSort,
    categories,
    filteredAndSortedSubmissions,
    stats,
    rubrics,
    getMainDescription,
    sanitizeAIText,
    // Access control
    userHasAccess,
    userPillar,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
    userCategory,
  };
};
